import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotBlank, _notNil } from '@/littledash';
import type { Animal, AnimalApiId } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { ApiService } from '@/support/ApiService';

export type BenchlingWorklist = components['schemas']['BenchlingWorklistV1.schema'];
/** Shared by every delegated Benchling action, not just worklists. */
type BenchlingDelegatedError = components['schemas']['BenchlingDelegatedErrorV1.schema'];

/** The failure the endpoint reported, or undefined when the response was not one of ours. */
export interface BenchlingWorklistFailure {
  reason: BenchlingDelegatedError['reason'];
  retryable: boolean;
}

/**
 * Whether every selected animal can actually be put in a worklist.
 *
 * The server enforces the synced half too - it is the same condition, checked there against the
 * database rather than the loaded rows. Checking here as well is purely so the action can be disabled
 * with an explanation instead of failing after the user commits to it.
 *
 * `api_id` is required as well as `benchling_entity_id` because it is what the request is built from.
 * Gating on only one of the two let a selection pass while the payload silently shrank: the modal
 * promised N animals, fewer were posted, and the only clue was the count in the success toast.
 */
export const allAnimalsSynced = (animals: Array<Animal>): boolean =>
  animals.every((animal) => _notNil(animal.api_id) && _isNotBlank(animal.benchling_entity_id ?? undefined));

/**
 * Creates a Benchling worklist from the selected animals, attributed to the acting user.
 *
 * Errors are thrown rather than toasted here so the caller can distinguish a 428 (the user has no
 * Benchling link) from an upstream failure and word the two differently.
 */
export const createBenchlingWorklist = async (
  studyApiId: StudyApiId,
  name: string,
  animals: Array<Animal>
): Promise<BenchlingWorklist> => {
  const response = await ApiService.call({
    endpoint: 'POST /api/v1/studies/{studyApiId}/benchling-worklists',
    path: { studyApiId },
    body: { name, animal_api_ids: animalApiIds(animals) },
    options: {
      // preserveOriginalError keeps the response on the thrown error; without it ApiService wraps it in
      // an InVivoError and the endpoint's reason code — the whole basis for wording 428 differently — is
      // no longer reachable.
      onError: {
        throw: true,
        preserveOriginalError: true,
        capture: false,
        toast: false,
        slug: 'benchling-worklist-create',
      },
    },
  });

  if (response.type !== 'success') {
    throw new Error('Failed to create Benchling worklist');
  }
  return response.body;
};

/** api_id is optional on the model, so narrow in one pass rather than mapping then filtering. */
const animalApiIds = (animals: Array<Animal>): Array<AnimalApiId> =>
  animals.reduce<Array<AnimalApiId>>((ids, animal) => {
    if (_notNil(animal.api_id)) {
      // Animal types api_id as a plain string, so the prefix is asserted at this boundary.
      ids.push(animal.api_id as AnimalApiId);
    }
    return ids;
  }, []);

/**
 * Pulls the endpoint's failure classification out of an axios-shaped error.
 *
 * `retryable` comes from the server rather than being inferred from the reason, so the two cannot
 * drift: the server owns which failures are worth another attempt.
 */
export const benchlingWorklistFailure = (error: unknown): BenchlingWorklistFailure | undefined => {
  const data = (error as { response?: { data?: Partial<BenchlingDelegatedError> } })?.response?.data;
  if (!_notNil(data?.reason)) {
    return undefined;
  }
  return { reason: data.reason, retryable: data.retryable ?? false };
};

/**
 * What to tell the user for each failure the endpoint can report.
 *
 * Each case is worded around the action the user can take, which is the whole reason the server
 * classifies these separately — a permission problem and a rate limit both used to arrive as one
 * opaque failure, and inviting a retry on the former sends the user nowhere.
 */
export const benchlingWorklistFailureMessage = (failure: BenchlingWorklistFailure): string => {
  switch (failure.reason) {
    case 'benchling_link_required':
      return 'Link your Benchling account from your account page before creating a worklist.';
    case 'benchling_permission_denied':
      return 'Your Benchling account does not have permission to create this worklist.';
    case 'benchling_rate_limited':
      return 'Benchling is rate limiting requests. Please try again in a moment.';
    case 'benchling_unavailable':
      return 'Benchling could not be reached. Please try again shortly.';
    case 'benchling_request_rejected':
    default:
      return 'Benchling rejected this worklist. Please contact support.';
  }
};
